import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import { FaHome, FaUsers, FaTools, FaInfoCircle, FaBars, FaTag, FaUserCircle, FaChartLine, FaCog, FaDatabase, FaChevronDown, FaSignOutAlt, FaUserCog, FaFileAlt, FaGift } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Cookies from 'js-cookie';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isMobileAccountDropdownOpen, setIsMobileAccountDropdownOpen] = useState(false);
  const [isUserIconDropdownOpen, setIsUserIconDropdownOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userLevel = Cookies.get('level') || 'trial';
  const accountDropdownRef = useRef(null);
  const mobileAccountDropdownRef = useRef(null);
  const userIconDropdownRef = useRef(null);
  const userEmail = Cookies.get('email') || '';
  
  // Extract only the username part of the email (before @)
  const userName = userEmail.split('@')[0] || '';

  // Reset dropdown state when authentication status changes
  useEffect(() => {
    setIsAccountDropdownOpen(false);
    setIsMobileAccountDropdownOpen(false);
    setIsUserIconDropdownOpen(false);
  }, [isAuthenticated]);

  // Close dropdowns when route changes
  useEffect(() => {
    setIsAccountDropdownOpen(false);
    setIsMobileAccountDropdownOpen(false);
    setIsUserIconDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close user icon dropdown when opening mobile menu
    if (!isMobileMenuOpen) {
      setIsUserIconDropdownOpen(false);
    }
  };

  const toggleUserIconDropdown = () => {
    setIsUserIconDropdownOpen(!isUserIconDropdownOpen);
    // Close mobile menu when opening user icon dropdown
    if (!isUserIconDropdownOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const toggleMobileAccountDropdown = () => {
    setIsMobileAccountDropdownOpen(!isMobileAccountDropdownOpen);
  };

  const handleMobileMenuClick = (e) => {
    // If the click is on the logout button, don't close the mobile menu
    if (e && e.target.closest('.dropdown-item') && e.target.closest('.dropdown-item').textContent.trim() === '登出') {
      console.log('Click detected on logout button in mobile menu, not closing menu');
      return;
    }
    setIsMobileMenuOpen(false);
  };

  const toggleAccountDropdown = () => {
    setIsAccountDropdownOpen(!isAccountDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on the logout button
      if (event.target.closest('.dropdown-item') && event.target.closest('.dropdown-item').textContent.trim() === '登出') {
        console.log('Click detected on logout button, not closing dropdown');
        return;
      }
      
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setIsAccountDropdownOpen(false);
      }
      if (mobileAccountDropdownRef.current && !mobileAccountDropdownRef.current.contains(event.target)) {
        setIsMobileAccountDropdownOpen(false);
      }
      if (userIconDropdownRef.current && !userIconDropdownRef.current.contains(event.target)) {
        setIsUserIconDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Render menu items based on user level
  const renderMenuItems = (isMobile = false) => {
    const commonItems = [
      <li key="stocklist">
        <Link to="/stocklist" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
          <span><strong>股東會列表</strong></span>
          {isMobile && <FaUsers />}
        </Link>
      </li>,
      // <li key="price">
      //   <Link to="/price" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
      //     <span><strong>定價</strong></span>
      //     {isMobile && <FaTag />}
      //   </Link>
      // </li>,
      <li key="solutions">
        <Link to="/feature" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
          <span><strong>功能</strong></span>
          {isMobile && <FaTools />}
        </Link>
      </li>,
      <li key="about">
        <Link to="/about" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
          <span><strong>關於我們</strong></span>
          {isMobile && <FaInfoCircle />}
        </Link>
      </li>
    ];

    // Superman level items (only superman sees these)
    const supermanItems = userLevel === 'superman' ? [
      <li key="admin">
        <Link to="/admin" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
          <span><strong>管理</strong></span>
          {isMobile && <FaCog />}
        </Link>
      </li>,
      <li key="database">
        <Link to="/database" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
          <span><strong>資料庫</strong></span>
          {isMobile && <FaDatabase />}
        </Link>
      </li>
    ] : [];

    // Authentication items - only show in desktop view
    const authItems = !isMobile && isAuthenticated ? [
      <li key="account-dropdown" className="account-dropdown-container nav-item" ref={accountDropdownRef}>
        <button 
          className="nav-link account-dropdown-button" 
          onClick={toggleAccountDropdown}
        >
          <span><strong>{userName}</strong></span>
          <FaChevronDown className={`dropdown-icon ${isAccountDropdownOpen ? 'open' : ''}`} />
        </button>
        {isAccountDropdownOpen && (
          <div className="account-dropdown-menu" onClick={(e) => e.stopPropagation()}>
            <Link to="/phyidlist" className="dropdown-item" onClick={(e) => {
              handleMobileMenuClick(e);
              setIsAccountDropdownOpen(false);
            }}>
              <span>身分證列表</span>
            </Link>
            <Link to="/voteprint" className="dropdown-item" onClick={(e) => {
              handleMobileMenuClick(e);
              setIsAccountDropdownOpen(false);
            }}>
              <span>電投表下載</span>
            </Link>
            <Link to="/gifts" className="dropdown-item" onClick={(e) => {
              handleMobileMenuClick(e);
              setIsAccountDropdownOpen(false);
            }}>
              <span>我的禮券</span>
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Logout button clicked directly');
                handleLogout();
              }} 
              className="dropdown-item"
            >
              <span>登出</span>
            </button>
          </div>
        )}
      </li>
    ] : !isAuthenticated ? [
      <li key="login">
        <Link to="/login" className="nav-link" onClick={isMobile ? handleMobileMenuClick : undefined}>
          <span><strong>登入</strong></span>
          {isMobile && <FaUserCircle />}
        </Link>
      </li>
    ] : [];

    return [...commonItems, ...supermanItems, ...authItems];
  };

  return (
    <>
      <div className="header-wrapper">
        <header className="header">
          <div className="logo-container">
            <Link to="/">
              <img src="/logo.jpg" alt="ErnieToTech Logo" className="logo" />
            </Link>
            {/* <span className="company-name">ErnieToTech</span> */}
          </div>
          
          {/* Desktop Navigation */}
          <nav className="main-nav desktop-nav">
            <ul>
              {renderMenuItems(false)}
            </ul>
          </nav>  

          {/* Mobile Menu Button */}
          <div className="mobile-buttons">
            {isAuthenticated && (
              <div className="user-icon-dropdown-container" ref={userIconDropdownRef}>
                <button className="mobile-menu-button user-icon-button" onClick={toggleUserIconDropdown}>
                  <FaUserCircle />
                </button>
                {isUserIconDropdownOpen && (
                  <div className="user-icon-dropdown-menu">
                    <div className="dropdown-header">
                      <FaUserCircle className="user-icon" />
                      <span>{userName}</span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <Link to="/phyidlist" className="dropdown-item" onClick={(e) => {
                      handleMobileMenuClick(e);
                      setIsAccountDropdownOpen(false);
                    }}>
                      <FaUserCog className="dropdown-icon" />
                      <span>身分證列表</span>
                    </Link>
                    <Link to="/voteprint" className="dropdown-item" onClick={(e) => {
                      handleMobileMenuClick(e);
                      setIsAccountDropdownOpen(false);
                    }}>
                      <FaFileAlt className="dropdown-icon" />
                      <span>電投表下載</span>
                    </Link>
                    <Link to="/gifts" className="dropdown-item" onClick={(e) => {
                      handleMobileMenuClick(e);
                      setIsAccountDropdownOpen(false);
                    }}>
                      <FaGift className="dropdown-icon" />
                      <span>我的禮券</span>
                    </Link>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Logout button clicked directly');
                        handleLogout();
                      }} 
                      className="dropdown-item"
                    >
                      <FaSignOutAlt className="dropdown-icon" />
                      <span>登出</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <button className="mobile-menu-button" onClick={toggleMobileMenu}>
              <FaBars />
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
            <ul>
              {renderMenuItems(true)}
            </ul>
          </nav>
        </header>
      </div>
      <div className="header-spacer"></div>
    </>
  );
};

export default Header; 